import Phaser from "phaser";
import { BaseModal } from "@phaser/ui/modals/BaseModal";
import { HelpModal } from "@phaser/ui/modals/HelpModal";
import { PauseModal } from "@phaser/ui/modals/PauseModal";
import type { GameStateController } from "@/phaser/core/game/GameStateController";
import { MonsterCardModal } from "@phaser/ui/modals/MonsterCardModal";
import { PurchaseModal } from "@phaser/ui/modals/PurchaseModal";

export const ModalType = {
  HELP: "HELP",
  PAUSE: "PAUSE",
  MONSTER_CARD: "MONSTER_CARD",
  PURCHASE: "PURCHASE",
} as const;

export type ModalType = (typeof ModalType)[keyof typeof ModalType];

export interface MonsterCardModalPayload {
  monsterId: string;
}

export interface PurchaseModalPayload {
  itemId: string;
}

export class ModalManager {
  private scene: Phaser.Scene;
  private state?: GameStateController;
  private current?: BaseModal;

  constructor(scene: Phaser.Scene, state?: GameStateController) {
    this.scene = scene;
    this.state = state;
  }

  open(
    type: ModalType,
    payload?: MonsterCardModalPayload | PurchaseModalPayload,
  ) {
    const { width, height } = this.scene.scale;

    // 이미 열려있으면 닫고 교체
    this.close();

    switch (type) {
      case ModalType.HELP:
        this.current = new HelpModal(this.scene, width, height, () => {
          this.current = undefined;
        });
        break;
      case ModalType.PAUSE:
        if (!this.state) {
          throw new Error("ModalManager: state is required for PAUSE modal.");
        }

        this.current = new PauseModal(
          this.scene,
          width,
          height,
          this.state,
          () => {
            this.current = undefined;
          },
        );
        break;

      case ModalType.MONSTER_CARD:
        if (!payload || !("monsterId" in payload)) {
          throw new Error(
            "ModalManager: payload is required for MONSTER_CARD modal.",
          );
        }

        this.current = new MonsterCardModal(
          this.scene,
          width,
          height,
          payload,
          () => {
            this.current = undefined;
          },
        );
        break;

      case ModalType.PURCHASE:
        const modal = new PurchaseModal(this.scene, width, height, () => {
          this.scene.events.emit("purchase-confirm-closed");
        });

        if (payload && "itemId" in payload) {
          modal.setItem(payload.itemId);
        }

        this.current = modal;
        break;

      default:
        throw new Error(`Unknown modal type: ${type}`);
    }

    this.current.open();
  }

  close() {
    if (!this.current) return;
    this.current.close();
    this.current = undefined;
  }

  isOpen() {
    return !!this.current;
  }
}
